"use client";

import { useCallback } from "react";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Check, LogOut, Plus, Settings as SettingsIcon, ShieldCheck } from "lucide-react";

import { NamespaceIcon } from "@/components/scirix/namespace-icon";
import { UserAvatar } from "@/components/scirix/user-avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NAMESPACE_QUERY_PARAM } from "@/hooks/useNamespaceQueryParamHydrator";
import { useActiveNamespaceId, useNamespacesQuery } from "@/hooks/useNamespaces";
import { useOptionalSession } from "@/hooks/useSession";
import { logoutAndRedirect } from "@/lib/session/client";
import type { SessionProfile } from "@/lib/session/types";
import { cn } from "@/lib/utils";
import { useNamespaceStore } from "@/state/namespace-store";
import type { NamespaceId, NamespaceSummary } from "@/types/namespaces";

// All workspace routes carry the slug as the first path segment, e.g.
// `/Scirix/tasks` or `/Scirix/governance/snapshots/<viewId>`.
// We swap that first segment when the user picks a different namespace.
const NAMESPACE_PATH_PATTERN = /^\/([^/]+)(\/.*)?$/;

// URL search params whose values are scoped to the active namespace and so
// don't make sense in a different one. Stripped when switching namespaces
// from the menu — leaving e.g. `task=1038` from bse in the URL when moving to
// my-very-first-project produces a 404 and triggers cascading recovery in the
// task adapter that rewrites the URL back to the old namespace.
const NAMESPACE_SCOPED_QUERY_PARAMS = ["task", "docPath", "path", "base", "head"] as const;

// Top-level routes that are NOT namespace-scoped. When the current path starts
// with one of these, the slug-swap path rewrite is skipped (we update the
// store-only and let the next workspace navigation pick up the change).
const NON_NAMESPACE_PATH_PREFIXES = new Set([
  "login",
  "oauth",
  "onboarding",
  "api",
  "design-system",
  "themed-shadcn",
  "tokens",
  "layout-tokens",
  "layout-cheat-sheet",
  "dev",
  "cli",
  "preview",
  "react-flow",
  "system",
  "workspace", // legacy; routes deleted but URL might still be in transit
  "admin",
]);

// Scopes that surface the admin console entry. Kept in step with ADMIN_SCOPES
// in app/(app)/admin/layout.tsx, which enforces the actual gate.
const ADMIN_MENU_SCOPES = ["admin:organizations", "admin:users"] as const;

export function TopBarUserMenu() {
  const sessionContext = useOptionalSession();

  if (!sessionContext || sessionContext.session.status !== "authenticated") {
    return null;
  }

  return <TopBarUserMenuContent profile={sessionContext.session.profile} />;
}

function TopBarUserMenuContent({ profile }: { profile: SessionProfile }) {
  const namespacesQuery = useNamespacesQuery();
  const activeNamespaceId = useActiveNamespaceId();
  const setSelectedNamespaceId = useNamespaceStore((state) => state.setSelectedNamespaceId);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const displayName = profile.name?.trim() || profile.email || "Signed in user";
  const triggerLabel = `Open account menu for ${displayName}`;

  const namespaces = namespacesQuery.data ?? [];
  const scopes = profile.scopes ?? [];
  const hasAdminAccess = ADMIN_MENU_SCOPES.some((scope) => scopes.includes(scope));

  const handleSelectNamespace = useCallback(
    (namespaceId: NamespaceId) => {
      if (namespaceId === activeNamespaceId) return;

      setSelectedNamespaceId(namespaceId);

      const params = new URLSearchParams(searchParams ? searchParams.toString() : "");
      // Drop URL params that are scoped to the previous namespace. Same idea
      // for `task` / `docPath` / `path` / `base` / `head` in tasks/documents/files —
      // carrying them across namespaces 404s and triggers cascading recovery.
      for (const key of NAMESPACE_SCOPED_QUERY_PARAMS) {
        params.delete(key);
      }
      // Strip the legacy `?namespaceId=` if it's still in the URL.
      params.delete(NAMESPACE_QUERY_PARAM);

      const targetNamespace = namespaces.find((n) => n.namespaceId === namespaceId);
      const targetSlug = targetNamespace?.slug;

      const match = pathname?.match(NAMESPACE_PATH_PATTERN);
      const firstSegment = match?.[1] ?? null;
      const isNamespaceScopedPath = Boolean(
        firstSegment && !NON_NAMESPACE_PATH_PREFIXES.has(firstSegment),
      );

      if (isNamespaceScopedPath && targetSlug) {
        const rest = match?.[2] ?? "";
        const qs = params.toString();
        const suffix = qs ? `?${qs}` : "";
        router.push(`/${encodeURIComponent(targetSlug)}${rest}${suffix}`);
        return;
      }

      // For non-namespace paths (e.g. /onboarding), just keep the route and
      // let the store update propagate. We don't write the namespaceId into
      // the URL anymore — slug routing is the source of truth.
      if (pathname) {
        const qs = params.toString();
        router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
      }
    },
    [activeNamespaceId, namespaces, pathname, router, searchParams, setSelectedNamespaceId],
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={triggerLabel}
          title={displayName}
          className={cn(
            "group relative inline-flex h-8 w-8 items-center justify-center rounded-full outline-none",
            "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--token-foundation-surface-base)]",
          )}
        >
          <UserAvatar
            name={profile.name}
            email={profile.email}
            pictureUrl={profile.pictureUrl}
            className={cn(
              "h-full w-full text-[11px] transition-colors",
              "group-hover:bg-[color-mix(in_oklab,var(--token-surface-default)_30%,transparent)]",
              "group-data-[state=open]:bg-[color-mix(in_oklab,var(--token-surface-default)_30%,transparent)]",
            )}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="min-w-[260px]">
        <DropdownMenuLabel className="flex flex-col gap-0.5 py-2">
          <span className="truncate text-sm font-semibold text-foreground">{displayName}</span>
          {profile.email && profile.email !== displayName ? (
            <span className="truncate text-xs font-normal text-muted-foreground">
              {profile.email}
            </span>
          ) : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-[0.32em] text-muted-foreground/80">
          Namespaces
        </DropdownMenuLabel>
        <NamespaceList
          namespaces={namespaces}
          activeNamespaceId={activeNamespaceId}
          isLoading={namespacesQuery.isLoading}
          isError={Boolean(namespacesQuery.error)}
          onSelect={handleSelectNamespace}
        />
        <DropdownMenuItem asChild className="gap-2 text-muted-foreground">
          <Link href="/onboarding?newNamespace=1">
            <Plus className="h-4 w-4" aria-hidden />
            <span>New namespace</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild className="gap-2">
          <Link href="/settings">
            <SettingsIcon className="h-4 w-4" aria-hidden />
            <span>Settings</span>
          </Link>
        </DropdownMenuItem>
        {hasAdminAccess ? (
          <DropdownMenuItem asChild className="gap-2">
            <Link href="/admin">
              <ShieldCheck className="h-4 w-4" aria-hidden />
              <span>Admin</span>
            </Link>
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem
          className="gap-2"
          onSelect={(event) => {
            event.preventDefault();
            void logoutAndRedirect();
          }}
        >
          <LogOut className="h-4 w-4" aria-hidden />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type NamespaceListProps = {
  namespaces: NamespaceSummary[];
  activeNamespaceId: NamespaceId | null;
  isLoading: boolean;
  isError: boolean;
  onSelect: (namespaceId: NamespaceId) => void;
};

function NamespaceList({
  namespaces,
  activeNamespaceId,
  isLoading,
  isError,
  onSelect,
}: NamespaceListProps) {
  if (isLoading && namespaces.length === 0) {
    return (
      <DropdownMenuItem disabled className="text-xs text-muted-foreground">
        Loading namespaces…
      </DropdownMenuItem>
    );
  }

  if (isError && namespaces.length === 0) {
    return (
      <DropdownMenuItem disabled className="text-xs text-muted-foreground">
        Couldn’t load namespaces
      </DropdownMenuItem>
    );
  }

  if (namespaces.length === 0) {
    return (
      <DropdownMenuItem disabled className="text-xs text-muted-foreground">
        No namespaces available
      </DropdownMenuItem>
    );
  }

  return (
    <>
      {namespaces.map((ns) => {
        const isActive = ns.namespaceId === activeNamespaceId;
        return (
          <DropdownMenuItem
            key={ns.namespaceId}
            className="flex items-center justify-between gap-3"
            onSelect={() => onSelect(ns.namespaceId)}
          >
            <span className="flex min-w-0 items-center gap-2">
              <NamespaceIcon
                icon={ns.icon}
                name={ns.name}
                className="h-5 w-5 rounded-[5px] text-base"
              />
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-sm text-foreground">{ns.name}</span>
                {ns.organizationName ? (
                  <span className="truncate text-[11px] text-muted-foreground">
                    {ns.organizationName}
                  </span>
                ) : null}
              </span>
            </span>
            {isActive ? <Check className="h-4 w-4 text-foreground" aria-hidden /> : null}
          </DropdownMenuItem>
        );
      })}
    </>
  );
}
